import cityCountry from 'forms/shared/validators/models/location/models/cityCountry';
import { OTHER, DEFAULT_LATEST, DEFAULT_EARLIEST } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { DateBaseValidatorOptions } from 'forms/shared/validators/base/date/types';
import { REQUIRED } from 'constants/errors';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { foreignContactRelationshipOptions, foreignNationalWorkOptions } from 'constants/enums/foreignActivityOptions';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import { hasYesOrNo } from 'models/validate';
import { isSafeUnitedStates } from 'helpers/locations';
import { isPVQForm } from 'helpers/form';
import { SpouseForeignContactsItemModel } from '../types';
import { createPvqAliasesValidator } from './pvqAliasesValidator';

const frequencyOptions = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Annually', 'Other'];

// Spouse Foreign Contact validator is for PVQ only
export const spouseForeignContactsItemValidator: ValidateJSModel<SpouseForeignContactsItemModel> = {
  ListedPreviously: (value, attributes, attributeName, options) => ({
    presence: true, hasValue: { validator: hasYesOrNo }
  }),

  Name: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    return {
      presence: true,
      model: { validator: pvqNameModelValidator },
    };
  },

  LastContact: (value, attributes) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    const dateLimits: DateBaseValidatorOptions = {
      earliest: DEFAULT_EARLIEST,
      latest: DEFAULT_LATEST
    };

    return {
      presence: true,
      date: dateLimits,
    };
  },

  Frequency: (value, attributes) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    return {
      hasValue: {
        validator: {
          inclusion: frequencyOptions,
        },
      }
    };
  },

  FrequencyExplanation: (value, attributes) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    if (attributes?.Frequency?.value === 'Other') {
      return { hasValue: true };
    }

    return {};
  },

  Relationship: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    return {
      hasValue: {
        validator: { inclusion: foreignContactRelationshipOptions },
      },
    };
  },

  RelationshipExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    if (attributes?.Relationship?.value === 'Other') {
      return { hasValue: true };
    }

    return {};
  },

  Aliases: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    return {
      presence: true,
      branchCollection: {
        validator: createPvqAliasesValidator(value, attributes),
      },
    };
  },

  CitizenshipNotApplicable: {},

  Citizenship: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes' || attributes?.CitizenshipNotApplicable?.applicable === false) {
      return {};
    }

    return { hasValue: true, country: true };
  },

  Birthdate: (value, attributes) => {
    if (attributes?.ListedPreviously?.value === 'Yes' || attributes?.BirthdateNotApplicable?.applicable === false) {
      return {};
    }

    return {
      presence: true,
      date: OTHER,
    };
  },

  Birthplace: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes' || attributes?.BirthplaceNotApplicable?.applicable === false) {
      return {};
    }
    if (isPVQForm(options?.formType)) {
      return {
        location: {
          requireIsMilitaryDiplomaticAddress: false,
          requireStreet: false,
          requireCity: true,
          requireCountry: true,
          requireState: false,
          requireZipCode: false,
          validator: pvqLocationModelValidator,
        },
        presence: true,
      };
    }
    return {
      presence: true,
      location: {
        validator: cityCountry,
      },
    };
  },

  Address: (value, attributes, attributeName, options) => {
    if (
      attributes?.ListedPreviously?.value === 'Yes'
      || attributes?.AddressNotApplicable?.applicable === false
      || attributes?.InUsaNotApplicable?.applicable === false
    ) {
      return {};
    }

    return {
      presence: true,
      location: {
        validator: pvqLocationModelValidator,
        requireStreet: true,
        requireIsMilitaryDiplomaticAddress: true,
        requireMilitaryDiplomaticInstallationName: true,
        requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
      },
    };
  },

  BirthdateNotApplicable: {},

  BirthplaceNotApplicable: {},

  AddressNotApplicable: {},

  ForeignDisciplinesNotApplicable: {},

  ForeignDisciplines: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    if (attributes?.ForeignDisciplinesNotApplicable?.applicable === true) {
      return {
        presence: true,
        array: {
          length: { minimum: 1, message: REQUIRED },
          validator: { inclusion: foreignNationalWorkOptions },
        }
      };
    }

    return {};
  },

  ForeignDisciplinesOtherExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    if (attributes?.ForeignDisciplines?.values?.includes('Other Government Agency')) {
      return { hasValue: true };
    }

    return {};
  },

  ForeignAssociationDescription: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes' || attributes?.ForeignAssociationDescriptionNotApplicable?.applicable === false) {
      return {};
    }

    if (attributes?.ForeignDisciplines?.values?.includes('Military or Armed Forces')
      || attributes?.ForeignDisciplines?.values?.includes('Security Service')
      || attributes?.ForeignDisciplines?.values?.includes('Intelligence Service')
      || attributes?.ForeignDisciplines?.values?.includes('Defense Industry')
      || attributes?.ForeignDisciplines?.values?.includes('Foreign Movement (such as a social, political, or religious movement)')) {
      return { hasValue: true };
    }

    return {};
  },

  Obligations: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    return { hasValue: true };
  },

  SpouseRelationship: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    return {
      hasValue: {
        validator: { inclusion: foreignContactRelationshipOptions },
      },
    };
  },

  SpouseRelationshipExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.ListedPreviously?.value === 'Yes') {
      return {};
    }

    if (attributes?.SpouseRelationship?.value === 'Other') {
      return { hasValue: true };
    }

    return {};
  },

  InUsaNotApplicable: {},

};
